import as from 'anitomyscript'
import { type ClassValue, clsx } from 'clsx'
import { readable } from 'simple-store-svelte'
import { cubicOut } from 'svelte/easing'
import { derived, type Readable, type Unsubscriber, type Updater } from 'svelte/store'
import { twMerge } from 'tailwind-merge'

import SUPPORTS from './modules/settings/supports'

import type { TransitionConfig } from 'svelte/transition'

export function anitomyscript (input: string[]) {
  const fixed = input.map(name => {
    // add space between S01E01 and v2
    name = name.replace(/s(\d{2})e(\d{2})(v\d{1})/i, 'S$1E$2 $3')
    if (!name.includes(' ')) {
      // replace s01e01.A.wish.made with S01E01 A.wish.made, there's some bug in anitomyscript where it doesn't parse the episode number if there's a single character after the sxxexx
      return name.replace(/s(\d{2})e(\d{2})\.([A-z])\./i, 'S$1E$2 $3 ')
    }
    return name
  })

  return as(fixed)
}

export function assert (x: unknown): asserts x {
  if (!x) {
    throw new Error('Assertion failed.')
  }
}

export function cn (...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type MediaQuery<Query extends Record<string, string> = Record<string, string>> = {
  [K in keyof Query]?: boolean | string;
}

function calculateMedia (mqls: Record<string, MediaQueryList>) {
  const media: MediaQuery = {}
  for (const [key, query] of Object.entries(mqls)) {
    media[key] = query.matches
  }
  return media
}

const mediaQueries = {
  '4xs': '(min-width: 280px)',
  '3xs': '(min-width: 320px)',
  '2xs': '(min-width: 360px)',
  xs: '(min-width: 480px)',
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
  '3xl': '(min-width: 1920px)',
  '4xl': '(min-width: 2560px)',
  '5xl': '(min-width: 3840px)',
  '6xl': '(min-width: 5120px)'
} as const

export const breakpoints = readable<MediaQuery<typeof mediaQueries>>({}, set => {
  const ctrl = new AbortController()
  const mqls: Record<string, MediaQueryList> = {}
  const updateMedia = () => set(calculateMedia(mqls))
  for (const [key, query] of Object.entries(mediaQueries)) {
    mqls[key] = window.matchMedia(query)
    mqls[key].addEventListener('change', updateMedia, ctrl)
  }
  updateMedia()
  return () => ctrl.abort()
})

interface FlyAndScaleParams {
  y?: number
  x?: number
  start?: number
  duration?: number
  base?: string
}

export const flyAndScale = (
  node: Element,
  params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 }
): TransitionConfig => {
  const transform = params.base ?? (getComputedStyle(node).transform === 'none' ? '' : getComputedStyle(node).transform)

  const scaleConversion = (
    valueA: number,
    scaleA: [number, number],
    scaleB: [number, number]
  ) => {
    const [minA, maxA] = scaleA
    const [minB, maxB] = scaleB

    const percentage = (valueA - minA) / (maxA - minA)
    const valueB = percentage * (maxB - minB) + minB

    return valueB
  }

  const styleToString = (
    style: Record<string, number | string | undefined>
  ): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str
      return str + `${key}:${style[key]};`
    }, '')
  }

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0])
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0])
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1])

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t
      })
    },
    easing: cubicOut
  }
}

export const sleep = (t: number) => new Promise<void>(resolve => setTimeout(resolve, t))

export const highEntropyValues = 'userAgentData' in navigator && navigator.userAgentData?.getHighEntropyValues?.(['architecture', 'platform', 'platformVersion'])

export function safeLocalStorage<T> (key: string): T | undefined {
  try {
    const value = localStorage.getItem(key)
    if (value) return JSON.parse(value) as T
  } catch (e) {
    console.error(e)
  }
}

export const debounce = <T extends (...args: any[]) => unknown>(
  callback: T,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout>
  const fn = (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => callback(...args), waitFor)
  }
  fn.cancel = () => clearTimeout(timeout)
  return fn
}

const formatter = new Intl.RelativeTimeFormat('en')
const ranges: Partial<Record<Intl.RelativeTimeFormatUnit, number>> = {
  years: 3600 * 24 * 365,
  months: 3600 * 24 * 30,
  weeks: 3600 * 24 * 7,
  days: 3600 * 24,
  hours: 3600,
  minutes: 60,
  seconds: 1
}

export function since (date: Date) {
  const secondsElapsed = (date.getTime() - Date.now()) / 1000
  for (const _key in ranges) {
    const key = _key as Intl.RelativeTimeFormatUnit
    if ((ranges[key] ?? 0) < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / (ranges[key] ?? 0)
      return formatter.format(Math.round(delta), key)
    }
  }
  return 'now'
}
export function eta (seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s'

  const units = [
    { label: 'y', secs: 31536000 },
    { label: 'mo', secs: 2592000 },
    { label: 'd', secs: 86400 },
    { label: 'h', secs: 3600 },
    { label: 'm', secs: 60 },
    { label: 's', secs: 1 }
  ]

  let remaining = Math.floor(seconds)
  const parts: string[] = []

  for (const { label, secs } of units) {
    if (remaining >= secs) {
      const value = Math.floor(remaining / secs)
      parts.push(`${value}${label}`)
      remaining %= secs
      // Only show up to two largest units (e.g., "1h 2m", "2m 3s")
      if (parts.length === 2) break
    }
  }

  // If nothing matched, show "0s"
  return parts.length ? parts.join(' ') : '0s'
}
const bytes = [' B', ' kB', ' MB', ' GB', ' TB']
export function fastPrettyBytes (num: number) {
  if (isNaN(num)) return '0 B'
  if (num < 1) return num + ' B'
  const exponent = Math.min(Math.floor(Math.log(num) / Math.log(1000)), bytes.length - 1)
  return Number((num / Math.pow(1000, exponent)).toFixed(1)) + bytes[exponent]!
}

const bits = [' b', ' kb', ' Mb', ' Gb', ' Tb']
export function fastPrettyBits (num: number) {
  if (isNaN(num)) return '0 b'
  if (num < 1) return num + ' b'
  const exponent = Math.min(Math.floor(Math.log(num) / Math.log(1000)), bits.length - 1)
  return Number((num / Math.pow(1000, exponent)).toFixed(1)) + bits[exponent]!
}

export function toTS (sec: number, full?: number) {
  if (isNaN(sec) || sec < 0) {
    switch (full) {
      case 1:
        return '0:00:00.00'
      case 2:
        return '0:00:00'
      case 3:
        return '00:00'
      default:
        return '0:00'
    }
  }
  const hours = Math.floor(sec / 3600)
  let minutes: string | number = Math.floor(sec / 60) - hours * 60
  let seconds: string | number = full === 1 ? (sec % 60).toFixed(2) : Math.floor(sec % 60)
  if (minutes < 10 && (hours > 0 || full)) minutes = '0' + minutes
  if (Number(seconds) < 10) seconds = '0' + seconds
  return (hours > 0 || full === 1 || full === 2) ? hours + ':' + minutes + ':' + seconds : minutes + ':' + seconds
}

export interface TraceAnime {
  anilist: number
  filename: string
  episode: number
  from: number
  to: number
  similarity: number
  video: string
  image: string
}

export async function traceAnime (image: File | string) { // WAIT lookup logic
  let res: Response
  if (image instanceof File) {
    res = await fetch('https://api.trace.moe/search?cutBorders', {
      method: 'POST',
      body: image,
      headers: { 'Content-type': image.type }
    })
  } else {
    res = await fetch(`https://api.trace.moe/search?cutBorders&url=${image}`)
  }
  const { result } = await res.json() as { result: TraceAnime[] }

  if (result.length) {
    return result
  } else {
    throw new Error('Search Failed\nCouldn\'t find anime for specified image! Try to remove black bars, or use a more detailed image.')
  }
}

export function codeToEmoji (c: string) {
  if (c === 'ALL') return '🌎'
  return c.replace(/./g, (ch) => String.fromCodePoint(0x1f1a5 + ch.charCodeAt(0)))
}

export class HashMap<K extends object, T> {
  map = new Map<string, T>()

  _id (k: K): string {
    return JSON.stringify(k, Object.keys(k).sort())
  }

  has (k: K): boolean {
    return this.map.has(this._id(k))
  }

  add (k: K, o: T) {
    return this.map.set(this._id(k), o)
  }

  delete (k: K): boolean {
    return this.map.delete(this._id(k))
  }

  clear () {
    this.map.clear()
  }

  get size (): number {
    return this.map.size
  }

  values (): IterableIterator<T> {
    return this.map.values()
  }

  [Symbol.iterator] (): IterableIterator<T> {
    return this.values()
  }
}

export const subtitleExtensions = ['srt', 'vtt', 'ass', 'ssa', 'sub', 'txt']
export const subRx = new RegExp(`.(${subtitleExtensions.join('|')})$`, 'i')

export const videoExtensions = ['3g2', '3gp', 'asf', 'avi', 'dv', 'flv', 'gxf', 'm2ts', 'm4a', 'm4b', 'm4p', 'm4r', 'm4v', 'mkv', 'mov', 'mp4', 'mpd', 'mpeg', 'mpg', 'mxf', 'nut', 'ogm', 'ogv', 'swf', 'ts', 'vob', 'webm', 'wmv', 'wtv']
export const videoRx = new RegExp(`.(${videoExtensions.join('|')})$`, 'i')

export const fontExtensions = ['ttf', 'ttc', 'woff', 'woff2', 'otf', 'cff', 'otc', 'pfa', 'pfb', 'pcf', 'fnt', 'bdf', 'pfr', 'eot']
export const fontRx = new RegExp(`.(${fontExtensions.join('|')})$`, 'i')

export const safefetch = async <T> (_fetch: typeof fetch, ...args: Parameters<typeof fetch>): Promise<T | null> => {
  try {
    const res = await _fetch(...args)
    return await res.json()
  } catch (e) {
    return null
  }
}

export function arrayEqual <T> (a: T, b: T) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  return a.length === b.length && a.every((v, i) => v === b[i])
}

export function nextTick () {
  return new Promise<void>(resolve => queueMicrotask(resolve))
}

export function colors (hex = '#ffffff') {
  const bigint = parseInt(hex.slice(1), 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return { r, g, b }
}

export function scaleBlurFade (node: Element, { duration = 300 } = {}) {
  return {
    delay: 0,
    duration,
    easing: cubicOut,
    css: (t: number) => `
        transform: scale(${t});
        filter: blur(${(1 - t) * 4}px);
        opacity: ${t};
      `
  }
}

export function transferToFileList (e: { dataTransfer?: DataTransfer | null, clipboardData?: DataTransfer | null } & Event) {
  const promises = [...(e.dataTransfer ?? e.clipboardData)!.items].map(item => {
    const type = item.type
    return new Promise<File | { text: string, type: string }>(resolve => item.kind === 'string' ? item.getAsString(text => resolve({ text, type })) : resolve(item.getAsFile()!))
  })
  return Promise.all(promises)
}

export async function toSettled<U extends Error, T> (promises: Iterable<Promise<T>>): Promise<{ settled: T[], errors: U[] }> {
  const settled: T[] = []
  const errors: U[] = []
  for (const settle of await Promise.allSettled(promises)) {
    if (settle.status === 'fulfilled') {
      settled.push(settle.value)
    } else {
      errors.push(settle.reason as U)
    }
  }
  return { settled, errors }
}

export async function filterAsync<T> (arr: T[], predicate: (item: T) => Promise<boolean>): Promise<T[]> {
  const { settled } = await toSettled(arr.map(predicate))
  return arr.filter((_, i) => settled[i])
}

export function saveFile (data: string | Record<string, unknown>, name: string, ext = 'json') {
  if (typeof data !== 'string') {
    data = JSON.stringify(data, null, 2)
  }
  const a = document.createElement('a')
  a.href = !SUPPORTS.isAndroid ? URL.createObjectURL(new File([data], name, { type: `application/${ext}` })) : `data:text/${ext};charset=utf-8,` + encodeURIComponent(data)
  a.download = name + '.' + ext
  a.click()
  URL.revokeObjectURL(a.href)
  return navigator.clipboard.writeText(data)
}

export async function readFile () {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'application/json'
  input.click()
  await new Promise((resolve) => { input.onchange = resolve })
  if (!input.files || input.files.length === 0) return
  return JSON.parse(await input.files[0]!.text())
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Stores = Readable<any> | [Readable<any>, ...Array<Readable<any>>] | Array<Readable<any>>
type StoresValues<T> = T extends Readable<infer U> ? U : { [K in keyof T]: T[K] extends Readable<infer U> ? U : never }

// type _ConsumerKeys<T, F> = F extends (media: unknown) => unknown
//   ? keyof Parameters<F>[0] & keyof T
//   : never

// export type PickByConsumer<T, F extends (media: unknown) => unknown, Extra extends keyof T = never> =
//   Pick<T, _ConsumerKeys<T, F> | Extra>

// export type PickMediaConsumer<F extends (media: unknown) => unknown, Extra extends keyof Media = never> =
//   PickByConsumer<Media, F, Extra>

export function derivedArray<S extends Stores, T>(stores: S, fn: (values: StoresValues<S>) => T, initialValue?: T): Readable<T>
export function derivedArray<S extends Stores, T> (stores: S, fn: (values: StoresValues<S>, set: (value: T) => void, update: (fn: Updater<T>) => void) => Unsubscriber | undefined, initialValue?: T): Readable<T> {
  let previous = initialValue

  return derived<S, T>(stores, (values, set, update) => {
    const emit = (next: T) => {
      if (previous === undefined || !arrayEqual(previous, next)) {
        previous = next
        set(next)
      }
    }

    const result = fn(values, emit, update) as T

    if (Array.isArray(result)) {
      emit(result)
      return
    }

    return result as (() => void) | undefined
  }, initialValue)
}

export function derivedDeep<T, U> (store: Readable<T>, fn: (value: T) => U) {
  let previousValue: string

  return derived<Readable<T>, U>(store, (value: T, set) => {
    const newValue = fn(value)
    const stringified = JSON.stringify(newValue)

    if (previousValue !== stringified) {
      previousValue = stringified
      set(newValue)
    }
  })
}

export function derivedTeardown<S extends Stores, T>(
  stores: S,
  fn: (values: StoresValues<S>, set: (value: T) => void, update: (fn: Updater<T>) => void) => ((cleanup: boolean) => void) | undefined,
  initialValue?: T
): Readable<T>
export function derivedTeardown<S extends Stores, T>(
  stores: S,
  fn: (values: StoresValues<S>) => T,
  initialValue?: T
): Readable<T>
export function derivedTeardown<S extends Stores, T> (
  stores: S,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  fn: Function,
  initialValue?: T
): Readable<T> {
  const single = !Array.isArray(stores)
  const storesArray: Array<Readable<unknown>> = single ? [stores as Readable<unknown>] : (stores as Array<Readable<unknown>>)

  if (!storesArray.every(Boolean)) {
    throw new Error('derived() expects stores as input, got a falsy value')
  }

  const auto = fn.length < 2
  const noop = () => {}

  return readable<T>(initialValue, (set, update) => {
    let started = false
    const values: unknown[] = []
    let pending = 0
    let cleanup: ((teardown: boolean) => void) = noop

    const sync = () => {
      if (pending) return
      cleanup(false)
      const result = fn(single ? values[0] : values, set, update)
      if (auto) {
        set(result as T)
      } else {
        cleanup = typeof result === 'function' ? result : noop
      }
    }

    const unsubscribers = storesArray.map((store, i) =>
      store.subscribe(
        (value: unknown) => {
          values[i] = value
          pending &= ~(1 << i)
          if (started) sync()
        },
        () => {
          pending |= 1 << i
        }
      )
    )

    started = true
    sync()

    return () => {
      for (const unsub of unsubscribers) unsub()
      cleanup(true)
      started = false
    }
  })
}

export function skipFirst<T> (store: Readable<T>) {
  let first = true
  return {
    ...store,
    subscribe: (run: (value: T) => void) => {
      return store.subscribe((value) => {
        if (first) {
          first = false
          return
        }
        run(value)
      })
    }
  }
}
